import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";

import { captureLead, captureLeadBeacon } from "@/api/lead";
import {
  buildMobileOtpLeadPayload,
  type FormValues,
  type ResolvedLeadAttribution,
} from "@/hooks/useCreateLead";
import { RootState } from "@/store/store";

const INDIAN_PHONE_REGEX = /^[6-9]\d{9}$/;
const FIRST_CAPTURE_DEBOUNCE_MS = 600;
const AUTOSAVE_DEBOUNCE_MS = 1500;

/**
 * One id per form-fill session. `crypto.randomUUID` where available; a cheap fallback otherwise
 * (non-secure/older contexts) — it only has to be unique per open form, not cryptographic.
 */
function newCaptureSessionId(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {
    /* fall through to the non-crypto fallback */
  }
  return `cs-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Additive, record-only "non-verified" capture. It does NOT change the host form's
 * flow: as soon as a valid phone is present it ensures a leads row and records the
 * partial fields (debounced), and on tab-close / abandon it flushes the latest
 * snapshot via sendBeacon. No OTP/SMS is sent here — the form's own OTP/submit flow
 * is untouched. The 15-min server sweep drops these as non-verified if never completed.
 *
 * Mount once per form, passing the form's current values. Safe to call with partial
 * values — only `phone` matters for activation; everything else is best-effort.
 *
 * `attribution` is omitted by every lead-drop widget, which keeps the widget branch of
 * `buildMobileOtpLeadPayload` and its pre-existing behaviour. Only the config-driven custom
 * form passes it, already resolved under its drop destination's rules. Because THIS hook is
 * what MINTS the row, a form whose attribution comes from the URL must pass it here as well
 * as on its submit — the server's back-fill only fills columns this capture left as
 * placeholders, so a row minted with the wrong value is never corrected.
 */
export function useNonVerifiedCapture(
  formValues: Record<string, unknown> | null | undefined,
  options?: {
    attribution?: ResolvedLeadAttribution | null;
    /**
     * The custom form this capture belongs to. Widgets omit it.
     *
     * Sent so the server can pin that form's drop destination onto the row AT CAPTURE.
     * The sweep routes by that pin, and this hook is what mints the row for a visitor who
     * types a phone and then leaves — the exact lead the sweep exists to rescue, and the
     * one that used to reach the ERP no matter which CRM its form was set to.
     */
    customLeadFormId?: number | null;
  }
) {
  const { university } = useSelector((state: RootState) => state.globalslice);
  const leadSubmitted = useSelector(
    (state: RootState) => state.globalslice.lead_submitted_successfully
  );

  const leadIdRef = useRef<string | null>(null);
  const latestRef = useRef<{ payload: Record<string, unknown>; valid: boolean }>({
    payload: {},
    valid: false,
  });
  // Serialized snapshot of the last payload we actually sent (autosave OR beacon). The abandon
  // beacon fires on EVERY tab-hide (visibilitychange → hidden), not just final close, and it
  // re-sends the latest snapshot even when nothing changed. Server-side, a capture re-arms the
  // non-verified drop (rearmNonVerifiedDrop) — so an unchanged beacon on a tab switch would
  // resurrect an already-dropped lead into the sweep and re-drop it (duplicate "Re-Enquired") on
  // every switch. We only send when the snapshot actually changed = genuine re-engagement.
  const lastSentRef = useRef<string>("");
  // One stable id per form-fill session (per hook mount). Sent with every capture so the server
  // keeps all edits — including a changed mobile/email mid-fill — on ONE row instead of forking.
  const captureSessionRef = useRef<string>("");
  if (!captureSessionRef.current) captureSessionRef.current = newCaptureSessionId();
  // A ref, like every other value the abandon flush reads: that effect has an empty dep
  // list so it can register its listeners once, and a value closed over directly would be
  // frozen at mount.
  const formIdRef = useRef<number | null>(null);
  formIdRef.current = options?.customLeadFormId ?? null;

  const phone = String(formValues?.phone ?? "").trim();
  const phoneValid = INDIAN_PHONE_REGEX.test(phone);
  // Stop ALL capture (debounced autosave AND the abandon beacon) once the user has
  // successfully submitted/verified a lead this session. The beacon fires on the
  // post-submit navigation (pagehide / visibilitychange → hidden); if it still ran, the
  // server's /leads/capture would call rearmNonVerifiedDrop and wipe the erp_drop_status
  // just set on the dropped lead — so the 15-min sweep would drop the SAME lead again and
  // create a duplicate ERP record. A completed lead is not a "non-verified" lead. Only a
  // genuinely-abandoned lead (which never sets this flag) stays eligible for re-engagement.
  const active = phoneValid && !leadSubmitted;
  const payload = formValues
    ? buildMobileOtpLeadPayload(
        formValues as unknown as FormValues,
        university,
        options?.attribution ?? null
      )
    : {};

  // Keep the refs fresh every render so the beacon always flushes the latest snapshot.
  latestRef.current = { payload, valid: active };

  const serialized = active ? JSON.stringify(payload) : "";

  // Debounced capture/autosave whenever the (active) snapshot changes.
  useEffect(() => {
    if (!active) return;
    const delay = leadIdRef.current ? AUTOSAVE_DEBOUNCE_MS : FIRST_CAPTURE_DEBOUNCE_MS;
    const timer = setTimeout(() => {
      void (async () => {
        const snapshot = JSON.stringify(latestRef.current.payload);
        const id = await captureLead(
          latestRef.current.payload,
          leadIdRef.current || undefined,
          captureSessionRef.current,
          formIdRef.current
        );
        // Only mark this snapshot "sent" on SUCCESS. A failed capture (network error → null) must
        // NOT latch lastSentRef, or the abandon beacon would skip it and the input would be lost.
        if (id) {
          leadIdRef.current = id;
          lastSentRef.current = snapshot;
        }
      })();
    }, delay);
    return () => clearTimeout(timer);
    // serialized already encodes the payload + activity; refs supply the latest value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialized, active]);

  // Flush the latest snapshot on abandon (tab close / background / navigation / form unmount).
  useEffect(() => {
    const flush = () => {
      if (!latestRef.current.valid) return;
      // Only flush genuinely NEW input. A bare tab-switch/minimize beacon that re-sends an
      // unchanged snapshot must not reach the server, or it re-arms and re-drops an already-dropped
      // lead ("Re-Enquired") on every hide. A real partial fill (typed but not yet autosaved) has a
      // changed snapshot and still flushes — so a genuine abandon is captured and re-dropped once.
      const snapshot = JSON.stringify(latestRef.current.payload);
      if (snapshot === lastSentRef.current) return;
      captureLeadBeacon(
        latestRef.current.payload,
        leadIdRef.current || undefined,
        captureSessionRef.current,
        formIdRef.current
      );
      lastSentRef.current = snapshot;
    };
    const onVisibility = () => {
      if (
        typeof document !== "undefined" &&
        document.visibilityState === "hidden"
      ) {
        flush();
      }
    };
    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("pagehide", flush);
      document.removeEventListener("visibilitychange", onVisibility);
      // Unmounting IS the end of this form-fill session — for a popup form that lives inside
      // `DialogContent` (lead drop 2 / 9 / 10) closing the dialog unmounts it, and neither
      // `pagehide` nor `visibilitychange` fires. Without this the debounced autosave timer is
      // cleared on the way out and the last ~1.5s of typing is lost, so an abandoned popup is
      // recorded short of what the user actually entered (or, if they never paused long enough
      // for a first autosave, not recorded at all).
      //
      // The same guards as every other flush path apply: nothing is sent unless a valid mobile
      // is present AND the lead has not already been submitted (`latestRef.current.valid`), and
      // an unchanged snapshot is skipped so a close that follows a completed autosave cannot
      // re-arm and re-drop the row a second time.
      flush();
    };
  }, []);
}
